import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Tooltip, useTheme } from '@mui/material';
import { DocumentArrowRightRegular } from '@fluentui/react-icons';
import { PageHeader } from '@ui/layout/Layout/components';
import { FiltersDiscardButton } from '@ui/components/common/buttons';
import SSearchInput from '@ui/components/common/SSearchInput';
import IncidentsDataGrid from './components/IncidentsDataGrid';
import useIncidentsStyles from './useIncidentsStyles';
import { useDispatch, useSelector } from 'react-redux';
import { clearFilters, fetchIncidentExcel, fetchIncidents, setSearch, resetIncidentState } from '@application/redux/Incidents/incident-slice';
const Incidents = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { saveDocumentButtonStyles } = useIncidentsStyles();
    const [isLoading, setIsLoading] = useState(false);
    const dispatch = useDispatch();
    // Refs to track mount state and prevent duplicate fetches
    const isInitialMountRef = useRef(true);
    const isMountedRef = useRef(true);
    const searchDebounceTimerRef = useRef(null);
    const fetchInProgressRef = useRef(false);
    // Track previous values to detect actual changes
    const prevDepsRef = useRef(null);
    const { incidents, search, pageNumber, pageSize, filteredColumns, sortField, sortOrder } = useSelector((state) => state.incidentDetail);
    const [isFilterEnabled, setIsFilterEnabled] = useState(false);
    // Serialize dependencies for comparison
    const depsKey = JSON.stringify({ search, pageNumber, pageSize, filteredColumns, sortField, sortOrder });
    /**
     * Core fetch function - handles loading state
     * No abort controller - let requests complete naturally
     */
    const doFetch = useCallback(async () => {
        // Prevent duplicate simultaneous fetches
        if (fetchInProgressRef.current) {
            return;
        }
        fetchInProgressRef.current = true;
        setIsLoading(true);
        try {
            await dispatch(fetchIncidents({
                requestVersion: Date.now() // Use timestamp as unique version
            })).unwrap();
        }
        catch (error) {
            // Only log non-cancellation errors
            if (error !== 'Request cancelled') {
                console.error('Fetch incidents failed:', error);
            }
        }
        finally {
            fetchInProgressRef.current = false;
            // Only update loading state if still mounted
            if (isMountedRef.current) {
                setIsLoading(false);
            }
        }
    }, [dispatch]);
    /**
     * SINGLE useEffect for all fetching logic
     * - Initial mount: fetch once
     * - Dependency change: fetch if values actually changed
     */
    useEffect(() => {
        isMountedRef.current = true;
        if (isInitialMountRef.current) {
            // First mount - do initial fetch
            isInitialMountRef.current = false;
            prevDepsRef.current = depsKey;
            doFetch();
        }
        else if (prevDepsRef.current !== depsKey) {
            // Dependencies actually changed - refetch
            prevDepsRef.current = depsKey;
            doFetch();
        }
        // Note: No cleanup for dependency changes - only on unmount
    }, [depsKey, doFetch]);
    /**
     * SEPARATE useEffect for unmount cleanup ONLY
     * This ensures state is reset when navigating away
     * Empty dependency array = runs cleanup only on unmount
     */
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            // Clear any pending debounce timers
            if (searchDebounceTimerRef.current) {
                clearTimeout(searchDebounceTimerRef.current);
            }
            // Use microtask to check if this is a real unmount (not StrictMode)
            queueMicrotask(() => {
                if (!isMountedRef.current) {
                    // CRITICAL: Reset ALL incident state on unmount
                    // This ensures the page opens fresh on next navigation
                    dispatch(resetIncidentState());
                }
            });
        };
    }, [dispatch]);
    const handleExcelSheetDownload = async () => {
        setIsLoading(true);
        try {
            await dispatch(fetchIncidentExcel()).unwrap();
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleEnableFilters = () => {
        setIsFilterEnabled(true);
    };
    const DiscardAndCloseFilters = () => {
        setIsFilterEnabled(false);
        dispatch(clearFilters());
    };
    const rows = React.useMemo(() => {
        if (!incidents?.data?.data)
            return [];
        return incidents.data.data.map((a) => ({
            id: a.id || '',
            systemName: a.systemName || '',
            assetName: a.assetName || '',
            licensePlate: a.licensePlate,
            location: a.location || '',
            locationQuardinates: a.locationQuardinates,
            incidentDate: a.incidentDate,
            score: a.score,
            imageUrl: a.imageUrl || ''
        }));
    }, [incidents?.data?.data]);
    const hasChanges = true;
    const hasNewChanges = isFilterEnabled && hasChanges;
    return (_jsxs(React.Fragment, { children: [_jsx(PageHeader, { title: t('Incidents'), icons: _jsx(_Fragment, { children: _jsx(Tooltip, { title: t('ExportIncidents'), placement: 'top', arrow: true, children: _jsx(Button, { size: 'small', variant: 'contained', color: 'primary', sx: saveDocumentButtonStyles, onClick: handleExcelSheetDownload, children: _jsx(DocumentArrowRightRegular, { color: theme.palette.primary.contrastText, fontSize: 20 }) }) }) }), filters: _jsxs(_Fragment, { children: [_jsx(FiltersDiscardButton, { hasNewChanges: hasNewChanges, handleDiscardFilters: DiscardAndCloseFilters, handleShowFilters: handleEnableFilters }), _jsx(SSearchInput, { onChange: (e) => {
                                const value = e.target.value;
                                if (searchDebounceTimerRef.current) {
                                    clearTimeout(searchDebounceTimerRef.current);
                                }
                                searchDebounceTimerRef.current = setTimeout(() => {
                                    dispatch(setSearch(value));
                                }, 1000);
                            } })] }) }), _jsx(IncidentsDataGrid, { handleEnableFilters: handleEnableFilters, showFilters: isFilterEnabled, rows: rows, loading: isLoading, fullHeight: true })] }));
};
export default Incidents;
